import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Gift, 
  Filter, 
  Package, 
  CheckCircle, 
  Calendar, 
  Star, 
  Clock, 
  Eye,
  UserCheck,
  AlertCircle
} from 'lucide-react';
import { RedeemedReward } from '../types/reward';
import type { RewardHistoryData } from '../types/reward';
import { rewardService } from '../services/rewardService';
import { userService } from '../services/userService';
import { useNotification } from '../contexts/NotificationContext';
import { formatDateForDisplay } from '../utils/dateUtils';

const Container = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  margin-bottom: 32px;
  border: 1px solid #f1f5f9;
`;

const Title = styled.h3`
  margin: 0 0 32px 0;
  font-size: 28px;
  font-weight: 800;
  background: linear-gradient(135deg, #F57C00 0%, #E65100 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.5px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
`;

const StatCard = styled(motion.div)`
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  border: 1px solid #e2e8f0;
`;

const StatNumber = styled.div`
  font-size: 32px;
  font-weight: 900;
  background: linear-gradient(135deg, #F57C00 0%, #E65100 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 8px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 600;
`;

const FilterSection = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterButton = styled(motion.button)<{ active: boolean }>`
  padding: 12px 20px;
  border: 2px solid ${(props: { active: boolean }) => props.active ? '#F57C00' : '#e2e8f0'};
  border-radius: 8px;
  background: ${(props: { active: boolean }) => props.active ? '#F57C00' : 'white'};
  color: ${(props: { active: boolean }) => props.active ? 'white' : '#374151'};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const RewardList = styled.div`
  display: grid;
  gap: 16px;
`;

const RewardCard = styled(motion.div)<{ status: 'redeemed' | 'used' | 'expired' | 'pending_approval' }>`
  padding: 24px;
  border-radius: 12px;
  border: 2px solid ${(props: { status: 'redeemed' | 'used' | 'expired' | 'pending_approval' }) => {
    switch (props.status) {
      case 'used': return '#10b981';
      case 'expired': return '#ef4444';
      case 'pending_approval': return '#3b82f6';
      default: return '#f59e0b';
    }
  }};
  background: ${(props: { status: 'redeemed' | 'used' | 'expired' | 'pending_approval' }) => {
    switch (props.status) {
      case 'used': return '#f0fdf4';
      case 'expired': return '#fef2f2';
      case 'pending_approval': return '#eff6ff';
      default: return '#fffbeb';
    }
  }};
  position: relative;
`;

const RewardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const RewardInfo = styled.div`
  flex: 1;
`;

const RewardName = styled.h4`
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
`;

const RewardDescription = styled.p`
  color: #64748b;
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
`;

const RewardStatus = styled.div<{ status: 'redeemed' | 'used' | 'expired' | 'pending_approval' }>`
  font-size: 12px;
  font-weight: 600;
  color: ${(props: { status: 'redeemed' | 'used' | 'expired' | 'pending_approval' }) => {
    switch (props.status) {
      case 'used': return '#059669';
      case 'expired': return '#dc2626';
      case 'pending_approval': return '#2563eb';
      default: return '#d97706';
    }
  }};
  padding: 6px 12px;
  background: white;
  border-radius: 6px;
  border: 1px solid currentColor;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const RewardDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 16px;
`;

const RewardDetail = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #64748b;
  font-weight: 500;
`;

const CreditsBadge = styled.span`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  color: #8B4513;
  display: flex;
  align-items: center;
  gap: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CategoryBadge = styled.span`
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: #e2e8f0;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 40px;
  color: #64748b;
`;

const UseRewardButton = styled(motion.button)`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
  }
`;

const ApprovalInfo = styled.div`
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  border: 1px solid #3b82f6;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: #1e40af;
`;

interface RewardHistoryProps {
  onClose: () => void;
}

const RewardHistory: React.FC<RewardHistoryProps> = ({ onClose }) => {
  const [rewardHistory, setRewardHistory] = useState<RewardHistoryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [usingReward, setUsingReward] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'redeemed' | 'used' | 'pending_approval'>('all');
  const { showSuccess, showError } = useNotification();

  useEffect(() => {
    loadRewardHistory();
  }, []);

  const loadRewardHistory = async () => {
    try {
      setLoading(true);
      const history = await rewardService.getRewardHistory();
      setRewardHistory(history);
    } catch (error) {
      console.error('Error loading reward history:', error);
      showError('Failed to load reward history');
    } finally {
      setLoading(false);
    }
  };

  const handleUseReward = async (rewardId: string) => {
    try {
      setUsingReward(rewardId);
      
      // Request approval from credit approvers
      await rewardService.requestRewardUsageApproval(rewardId);
      showSuccess('Reward usage request sent to your credit approvers. You will be notified when confirmed.');
      await loadRewardHistory(); // Reload data
    } catch (error) {
      console.error('Error using reward:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to use reward';
      showError(errorMessage);
    } finally {
      setUsingReward(null);
    }
  };

  const getStatusIcon = (status: 'redeemed' | 'used' | 'expired' | 'pending_approval') => {
    switch (status) {
      case 'used':
        return <CheckCircle size={16} />;
      case 'expired':
        return <Clock size={16} />;
      case 'pending_approval':
        return <UserCheck size={16} />;
      default:
        return <Package size={16} />;
    }
  };

  const getStatusText = (status: 'redeemed' | 'used' | 'expired' | 'pending_approval') => {
    switch (status) {
      case 'used':
        return 'Used';
      case 'expired':
        return 'Expired';
      case 'pending_approval':
        return 'Pending Confirmation';
      default:
        return 'Redeemed';
    }
  };

  const filteredRewards = rewardHistory?.rewards.filter(reward => {
    if (filter === 'all') return true;
    return reward.status === filter;
  }) || [];

  if (loading) {
    return (
      <Container>
        <div>Loading reward history...</div>
      </Container>
    );
  }

  return (
    <Container
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <Title>
        <Gift />
        My Wallet
      </Title>

      {rewardHistory && (
        <StatsGrid>
          <StatCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <StatNumber>{rewardHistory.totalRedeemed}</StatNumber>
            <StatLabel>Total Redeemed</StatLabel>
          </StatCard>
          <StatCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <StatNumber>{rewardHistory.totalUsed}</StatNumber>
            <StatLabel>Total Used</StatLabel>
          </StatCard>
          <StatCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <StatNumber>{rewardHistory.totalSpent}</StatNumber>
            <StatLabel>Total Spent</StatLabel>
          </StatCard>
          <StatCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <StatNumber>
              {rewardHistory.totalRedeemed > 0 
                ? Math.round(rewardHistory.totalSpent / rewardHistory.totalRedeemed) 
                : 0}
            </StatNumber>
            <StatLabel>Avg Cost</StatLabel>
          </StatCard>
        </StatsGrid>
      )}

      <FilterSection>
        <FilterButton
          active={filter === 'all'}
          onClick={() => setFilter('all')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Filter />
          All ({rewardHistory?.rewards.length || 0})
        </FilterButton>
        <FilterButton
          active={filter === 'redeemed'}
          onClick={() => setFilter('redeemed')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Package />
          Redeemed ({rewardHistory?.rewards.filter(r => r.status === 'redeemed').length || 0})
        </FilterButton>
        <FilterButton
          active={filter === 'pending_approval'}
          onClick={() => setFilter('pending_approval')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <UserCheck />
          Pending Confirmation ({rewardHistory?.rewards.filter(r => r.status === 'pending_approval').length || 0})
        </FilterButton>
        <FilterButton
          active={filter === 'used'}
          onClick={() => setFilter('used')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <CheckCircle />
          Used ({rewardHistory?.rewards.filter(r => r.status === 'used').length || 0})
        </FilterButton>
      </FilterSection>

      {filteredRewards.length === 0 ? (
        <EmptyState>
          <Gift size={48} />
          <h4>Wallet is Empty</h4>
          <p>
            {filter === 'all' 
              ? "You haven't redeemed any rewards yet. Start completing tasks to earn credits and fill your wallet!"
              : `No ${filter} rewards found in your wallet.`
            }
          </p>
        </EmptyState>
      ) : (
        <RewardList>
          {filteredRewards.map((reward) => (
            <RewardCard
              key={reward.id}
              status={reward.status}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <RewardHeader>
                <RewardInfo>
                  <RewardName>{reward.rewardName}</RewardName>
                  <RewardDescription>{reward.rewardDescription}</RewardDescription>
                </RewardInfo>
                
                <RewardStatus status={reward.status}>
                  {getStatusIcon(reward.status)}
                  {getStatusText(reward.status)}
                </RewardStatus>
              </RewardHeader>

              <RewardDetails>
                <RewardDetail>
                  <Calendar />
                  Redeemed: {formatDateForDisplay(reward.redeemedAt)}
                </RewardDetail>
                {reward.usedAt && (
                  <RewardDetail>
                    <CheckCircle />
                    Used: {formatDateForDisplay(reward.usedAt)}
                  </RewardDetail>
                )}
                <CreditsBadge>
                  <Star size={12} />
                  {reward.cost} Credits
                </CreditsBadge>
                <CategoryBadge>
                  {reward.category}
                </CategoryBadge>
              </RewardDetails>

              {reward.status === 'redeemed' && (
                <div style={{ marginTop: '16px' }}>
                  <ApprovalInfo>
                    <AlertCircle size={16} />
                    <span>Using this reward requires confirmation from your credit approvers.</span>
                  </ApprovalInfo>
                  <UseRewardButton
                    onClick={() => handleUseReward(reward.id)}
                    disabled={usingReward === reward.id}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <UserCheck size={14} />
                    {usingReward === reward.id ? 'Requesting Confirmation...' : 'Use'}
                  </UseRewardButton>
                </div>
              )}
            </RewardCard>
          ))}
        </RewardList>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', marginTop: '32px' }}>
        <FilterButton
          active={false}
          onClick={onClose}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Close
        </FilterButton>
      </div>
    </Container>
  );
};

export default RewardHistory; 